import { Fragment, type ReactNode } from 'react';

/**
 * Mostra um texto com a formatação do WhatsApp, como o cliente vê no celular:
 * *negrito*, _itálico_, ~riscado~, `código` / ```bloco```, listas com "- " e links clicáveis.
 * Gera elementos React (nunca HTML bruto), então o conteúdo da conversa não executa nada.
 */
export function WhatsAppText({ text }: { text: string }) {
  const lines = text.split('\n');
  const out: ReactNode[] = [];
  let list: ReactNode[] = [];
  const flushList = () => {
    if (!list.length) return;
    out.push(
      <ul key={`ul${out.length}`} className="my-0.5 space-y-0.5">
        {list}
      </ul>,
    );
    list = [];
  };
  lines.forEach((line, i) => {
    const bullet = /^[ \t]*[-*•][ \t]+(.*)$/.exec(line);
    if (bullet) {
      list.push(
        <li key={i} className="flex gap-2">
          <span aria-hidden>•</span>
          <span className="min-w-0">{inline(bullet[1]!)}</span>
        </li>,
      );
      return;
    }
    flushList();
    const quote = /^>[ \t]?(.*)$/.exec(line);
    if (quote) {
      out.push(
        <span key={i} className="my-0.5 block border-l-4 border-fg-4/40 pl-2 text-fg-2">
          {inline(quote[1]!)}
        </span>,
      );
      return;
    }
    // Linha vazia vira espaço entre parágrafos.
    out.push(line.trim() ? <span key={i} className="block">{inline(line)}</span> : <span key={i} className="block h-2" aria-hidden />);
  });
  flushList();
  return <>{out}</>;
}

// Marcador só vale colado ao texto e com borda de palavra antes/depois (como no WhatsApp).
const INLINE = /(```[\s\S]+?```|`[^`\n]+`|(?<![\p{L}\p{N}])\*(?!\s)[^*\n]+?(?<!\s)\*(?![\p{L}\p{N}])|(?<![\p{L}\p{N}])_(?!\s)[^_\n]+?(?<!\s)_(?![\p{L}\p{N}])|(?<![\p{L}\p{N}])~(?!\s)[^~\n]+?(?<!\s)~(?![\p{L}\p{N}])|https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/gu;

function inline(text: string, depth = 0): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const token = m[0];
    const at = m.index ?? 0;
    if (at > last) parts.push(text.slice(last, at));
    const key = `${depth}-${at}`;
    if (token.startsWith('```')) {
      parts.push(<code key={key} className="rounded bg-black/5 px-1 font-mono text-[0.92em]">{token.slice(3, -3)}</code>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={key} className="rounded bg-black/5 px-1 font-mono text-[0.92em]">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('http')) {
      parts.push(
        <a key={key} href={token} target="_blank" rel="noopener noreferrer" className="break-all text-[#027eb5] underline-offset-2 hover:underline" onClick={(e) => e.stopPropagation()}>
          {token}
        </a>,
      );
    } else {
      const inner = depth < 3 ? inline(token.slice(1, -1), depth + 1) : [token.slice(1, -1)];
      if (token[0] === '*') parts.push(<strong key={key} className="font-semibold">{inner}</strong>);
      else if (token[0] === '_') parts.push(<em key={key}>{inner}</em>);
      else parts.push(<s key={key}>{inner}</s>);
    }
    last = at + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => (typeof p === 'string' ? <Fragment key={`t${depth}-${i}`}>{p}</Fragment> : p));
}
